import type { HeroKey } from "@unbox/db";
import { XClose } from "@untitledui/icons";
import { type FC, useCallback, useRef, useState } from "react";
import { useDismissHero, useUserPreferences } from "@/api/user-preferences";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { cx } from "@/utils/cx";

export const PageHeroKey = {
  Cohorts: "cohorts",
  AllVideos: "all_videos",
  BlockedCreators: "blocked_creators",
  Rules: "rules",
  MessageTemplates: "message_templates",
  MessagesAndActivity: "messages_and_activity",
  AutomateOutreach: "automate_outreach",
  SampleRequests: "sample_requests",
  Reports: "reports",
  ConfigureSafety: "configure_safety",
  TargetCollabs: "target_collabs",
} as const satisfies Record<string, HeroKey>;
export type PageHeroKey = (typeof PageHeroKey)[keyof typeof PageHeroKey];

type PageHeroItem = { icon: FC<{ className?: string }>; message: string };

type PageHeroProps = {
  pageKey: PageHeroKey;
  title: string;
  text1: PageHeroItem;
  text2: PageHeroItem;
  text3: PageHeroItem;
  image: string;
  /**
   * `card` is cards on a transparent background, set inside the hero's own gradient with room above
   * and cut off at the bottom. It sits against the right edge until it reaches the text, then stays put
   * and is cut off on the right instead. `composition` is an image that already carries the gradient and fills
   * the hero's height.
   */
  imageLayout?: "card" | "composition";
};

export const PageHero = ({ pageKey, title, text1, text2, text3, image, imageLayout = "card" }: PageHeroProps) => {
  const { data, isSuccess } = useUserPreferences();
  const dismissHero = useDismissHero();
  const visible = isSuccess && !data.dismissedHeroes[pageKey];

  const onClose = () => dismissHero.mutate(pageKey);

  const imageSlotRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [cardOverflows, setCardOverflows] = useState(false);
  const measureOverflow = useCallback(() => {
    const slot = imageSlotRef.current;
    const img = imageRef.current;
    if (slot && img) setCardOverflows(img.offsetWidth > slot.clientWidth);
  }, []);
  useResizeObserver({ ref: imageSlotRef, onResize: measureOverflow });
  const imageCoversClose = imageLayout === "composition" || cardOverflows;

  return (
    <div
      aria-hidden={!visible}
      className={cx(
        "-mb-5 grid shrink-0 transition-[grid-template-rows,opacity] duration-300 ease-out",
        visible ? "grid-rows-[1fr] opacity-100" : "pointer-events-none grid-rows-[0fr] opacity-0",
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <section className="relative mb-5 flex overflow-hidden rounded-2xl border border-secondary bg-gradient-to-b from-[#2e1f3a] to-[#3e2356] py-9 pr-9 pl-[46px]">
          {imageLayout === "composition" && (
            <img
              aria-hidden
              alt=""
              src={image}
              className="pointer-events-none absolute top-0 right-0 h-full max-w-none object-cover object-left"
            />
          )}
          <div className="relative flex max-w-[600px] flex-col gap-5">
            <h2 className="text-xl font-medium text-white">{title}</h2>
            <ul className="flex flex-col gap-[14px]">
              {[text1, text2, text3].map(({ icon: Icon, message }) => (
                <li key={message} className="flex items-center gap-3 text-sm font-normal text-[#E8D8FF]">
                  <Icon className="size-5 shrink-0 text-[#E8D8FF]" />
                  {message}
                </li>
              ))}
            </ul>
          </div>
          {imageLayout === "card" && (
            <div className="relative -mt-2 -mb-9 ml-8 min-w-0 flex-1">
              <div ref={imageSlotRef} className="absolute inset-0">
                <img
                  ref={imageRef}
                  aria-hidden
                  alt=""
                  src={image}
                  onLoad={measureOverflow}
                  className="pointer-events-none ml-auto block h-full w-auto max-w-none"
                />
              </div>
            </div>
          )}
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onClose}
            tabIndex={visible ? 0 : -1}
            className={cx(
              "absolute top-[11px] right-3 inline-flex cursor-pointer items-center justify-center rounded-lg p-2 text-white transition-colors",
              imageCoversClose ? "bg-black/40 backdrop-blur-[2px] hover:bg-black/60" : "hover:bg-white/10",
            )}
          >
            <XClose className="size-5" aria-hidden />
          </button>
        </section>
      </div>
    </div>
  );
};
